import React from "react"

import { Floor } from "shared/types/Floor"
import { ProductPositionTypename, RoomTypename } from "shared/types/Project"
import { DragSource } from "shared/types/drag-and-drop/DragSource"
import { DropTarget } from "shared/types/drag-and-drop/DropTarget"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { TabBar } from "shared/ui/tab-bar/TabBar"
import { useTabBarContext } from "shared/ui/tab-bar/context/TabBarContext"

import { FilteredFloorActionHandlers } from "features/floor-management/model/useFloorActionHandlers"
import { useFloorActions } from "features/floor-management/model/useFloorActions"

import { useProjectPageTabDropTarget } from "./lib/useProjectPageTabDropTarget"

export interface ProjectPageFloorTabProps {
  floor: Floor
  index: number
  handlers: FilteredFloorActionHandlers | undefined
  onOpenDetails: () => void
  ariaControls?: string
  onDrop?: (dragSource: DragSource, dropTarget: DropTarget) => void
  truncationThreshold?: number
}

const ROOMS = [RoomTypename]
const ROOMS_AND_PRODUCTS = [RoomTypename, ProductPositionTypename]

export const ProjectPageFloorTab = React.memo(
  ({
    floor,
    handlers,
    onOpenDetails,
    onDrop,
    ariaControls,
    index,
    truncationThreshold = 15
  }: ProjectPageFloorTabProps) => {
    const { onTabSelect } = useTabBarContext()

    const { dropRef, isOver, canDrop } = useProjectPageTabDropTarget({
      index,
      floor,
      label: floor.label ?? "",
      eventKey: floor.id,
      onTabSelect,
      onDrop,
      hoverDelay: 200,
      enabled: true,
      acceptTypes: ROOMS_AND_PRODUCTS,
      directDropAcceptTypes: ROOMS
    })

    const { actions } = useFloorActions({
      idPrefix: `floor-tab-${floor.id}`,
      ...handlers,
      onOpenDetails
    })

    return (
      <div
        className="project-page-tab-bar__tab"
        data-drop-zone=""
        data-drop-zone-hover={isOver ? "" : undefined}
        data-drop-zone-active={isOver && canDrop ? "" : undefined}
        ref={dropRef}
      >
        <TabBar.Tab
          label={floor.label ?? ""}
          key={floor.id}
          eventKey={floor.id}
          index={index}
          dragSourceData={floor}
          icon={
            floor.hasClarificationRequest
              ? IconName.CLARIFICATION_REQUEST
              : undefined
          }
          truncationThreshold={truncationThreshold}
          variant={floor.hasClarificationRequest ? "warning" : "default"}
          aria-controls={ariaControls}
          aria-label={floor.label ?? undefined}
          actions={actions}
        />
      </div>
    )
  }
)

ProjectPageFloorTab.displayName = "ProjectPageFloorTab"
